import { useMemo } from "react";
import type { Job } from "@/data/types";
import { parseISO, isSameDay, isValid } from "date-fns";

export type ConflictType =
  | "crew_overlap"       // crew/sub assigned to multiple jobs same day
  | "no_crew"            // scheduled but no crew/sub assigned
  | "no_schedule"        // assigned crew/sub but no scheduled date
  | "unassigned";        // job active but completely unassigned

export interface SchedulingConflict {
  type: ConflictType;
  jobId: string;
  label: string;
  detail: string;
  /** Other job IDs involved (for overlap conflicts) */
  relatedJobIds?: string[];
}

/**
 * Detects scheduling & assignment conflicts across all active jobs.
 * Returns a map of jobId → conflicts[] plus a flat list of all conflicts.
 */
export function useSchedulingConflicts(jobs: Job[]) {
  return useMemo(() => {
    const conflicts: SchedulingConflict[] = [];
    const activeJobs = jobs.filter((j) => {
      if (j.currentStage === "Completed") return false;
      // Keep Sale Closed excluded for normal pipeline noise, but include it
      // when schedule/assignment anomalies exist so those jobs are not invisible.
      if (j.currentStage === "Sale Closed") {
        const hasSched = Boolean(j.scheduledInstallDate && isValid(parseISO(j.scheduledInstallDate)));
        const hasAssignee = Boolean(j.assignedCrew || j.assignedSubcontractor);
        return hasSched || hasAssignee;
      }
      return true;
    });

    // ── 1. Crew/sub overlap: same assignee, same date, multiple jobs ──
    const dateAssigneeMap = new Map<string, Job[]>();
    activeJobs.forEach((j) => {
      if (!j.scheduledInstallDate || !isValid(parseISO(j.scheduledInstallDate))) return;
      const assignee =
        j.assignmentType === "Internal Crew" ? j.assignedCrew :
        j.assignmentType === "Subcontractor" ? j.assignedSubcontractor : null;
      if (!assignee) return;
      const key = `${assignee}::${j.scheduledInstallDate}`;
      const list = dateAssigneeMap.get(key) || [];
      list.push(j);
      dateAssigneeMap.set(key, list);
    });

    const processedOverlapPairs = new Set<string>();
    dateAssigneeMap.forEach((jobList, key) => {
      if (jobList.length < 2) return;
      const [assignee] = key.split("::");
      const relatedIds = jobList.map((j) => j.id);
      jobList.forEach((j) => {
        const pairKey = `${j.id}::${key}`;
        if (processedOverlapPairs.has(pairKey)) return;
        processedOverlapPairs.add(pairKey);
        conflicts.push({
          type: "crew_overlap",
          jobId: j.id,
          label: "Scheduling Overlap",
          detail: `${assignee} has ${jobList.length} jobs on ${j.scheduledInstallDate}`,
          relatedJobIds: relatedIds.filter((id) => id !== j.id),
        });
      });
    });

    // ── 2. Scheduled but no crew/sub ──
    activeJobs.forEach((j) => {
      if (!j.scheduledInstallDate || !isValid(parseISO(j.scheduledInstallDate))) return;
      if (j.assignmentType === "Unassigned" || (!j.assignedCrew && !j.assignedSubcontractor)) {
        conflicts.push({
          type: "no_crew",
          jobId: j.id,
          label: "No Crew Assigned",
          detail: `Scheduled for ${j.scheduledInstallDate} but no crew or subcontractor assigned`,
        });
      }
    });

    // ── 3. Assigned but no schedule ──
    activeJobs.forEach((j) => {
      const hasAssignee =
        (j.assignmentType === "Internal Crew" && j.assignedCrew) ||
        (j.assignmentType === "Subcontractor" && j.assignedSubcontractor);
      if (!hasAssignee) return;
      if (!j.scheduledInstallDate || !isValid(parseISO(j.scheduledInstallDate))) {
        conflicts.push({
          type: "no_schedule",
          jobId: j.id,
          label: "No Schedule",
          detail: `Assigned to ${j.assignedCrew || j.assignedSubcontractor} but no install date scheduled`,
        });
      }
    });

    // Build per-job map
    const byJob = new Map<string, SchedulingConflict[]>();
    conflicts.forEach((c) => {
      const list = byJob.get(c.jobId) || [];
      list.push(c);
      byJob.set(c.jobId, list);
    });

    // Summary counts
    const overlapCount = new Set(conflicts.filter((c) => c.type === "crew_overlap").map((c) => c.jobId)).size;
    const noCrewCount = conflicts.filter((c) => c.type === "no_crew").length;
    const noScheduleCount = conflicts.filter((c) => c.type === "no_schedule").length;

    return {
      all: conflicts,
      byJob,
      getJobConflicts: (jobId: string) => byJob.get(jobId) || [],
      hasConflict: (jobId: string) => byJob.has(jobId),
      summary: { overlapCount, noCrewCount, noScheduleCount, total: conflicts.length },
    };
  }, [jobs]);
}
